import { reviveIntegersAsBigInt } from "./reviveIntegersAsBigInt";

// JSON keys whose values are nanosecond-epoch integers. The reviver allowlists
// by key name: a key listed here is re-read from its raw token as a `bigint`
// everywhere the reviver runs, and an integer under any other key stays the
// `Number` the default parse produced, already rounded if it ran past
// `Number.MAX_SAFE_INTEGER` (in ns-epoch, anything after ≈ April 15, 1970).
//
// - Topic and Event records: `start_time`, `end_time`.
// - Session aggregate bounds: `min_timestamp_ns`, `max_timestamp_ns`.
// - The session times a metric data point is placed at:
//   `session_start_time_ns`, `session_end_time_ns`.
// - The window of a file a session covers, both as the caller declares it (in
//   the file's own timestamps, `min_file_timestamp_ns` /
//   `max_file_timestamp_ns`, plus `anchor_ns`, the wall-clock instant that
//   data's time 0 sat at) and as the server reports it back
//   (`min_wall_clock_timestamp_ns` / `max_wall_clock_timestamp_ns`, plus the
//   `unix_epoch_offset_ns` it added to get there).
// - Magic-link context object: `t`.
export const NANOSECOND_TIMESTAMP_KEYS = new Set<string>([
  "start_time",
  "end_time",
  "min_timestamp_ns",
  "max_timestamp_ns",
  "session_start_time_ns",
  "session_end_time_ns",
  "min_file_timestamp_ns",
  "max_file_timestamp_ns",
  "min_wall_clock_timestamp_ns",
  "max_wall_clock_timestamp_ns",
  "anchor_ns",
  "unix_epoch_offset_ns",
  "t",
]);

/**
 * `JSON.parse` reviver that returns a `bigint` for an integer token under any
 * key in `NANOSECOND_TIMESTAMP_KEYS`, at any depth. A customer's user-metadata
 * can reuse one of these keys with a non-integer value, which comes back as
 * the default parse produced it.
 *
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/parse#the_reviver_parameter
 * https://github.com/tc39/proposal-json-parse-with-source
 */
export const parseTimestampsAsBigInt = reviveIntegersAsBigInt(
  NANOSECOND_TIMESTAMP_KEYS,
);
